import { request, type RequestOptions } from "@/utils/request";
import type { ShopAPI } from "@/api/typeings/shops";
import { setLanguageSetting, setLogoUrl, setReportTimezone, setSubscriptionUrl, updateCardsSetting, updatePaypalSetting, updateWalletsSetting, updateStorefrontAccessToken } from "@/api/settings";

/**
 * 获取店铺详情
 * @param params 店铺 {shopName: string}
 * @param options 请求配置
 */
export const getShopDetail = (params: any, options: RequestOptions = {}) => {
  // 新文档未提供 /api/shops/*，统一改用 /shopify-app/shop
  return request("/shopify-app/shop", {
    method: "GET",
    params: { ...params },
    ...options,
  });
};

/**
 * 更新店铺信息
 * @param params 店铺信息
 * @param body
 * @param options 请求配置
 */
export async function shopUpdate(
  _params: ShopAPI.ShopInfoParams,
  body: ShopAPI.ShopDto,
  options?: RequestOptions
) {
  // 旧的 shops 更新接口已废弃；这里按字段路由到新 /shopify-app/setting/* 接口
  if ((body as any)?.settings) {
    try {
      const settings = JSON.parse(String((body as any).settings || "{}"));
      if (settings.logo !== undefined && settings.logo !== null) {
        await setLogoUrl(String(settings.logo));
      }
      if (settings.language !== undefined || settings.isEnableConversionAPI !== undefined) {
        await setLanguageSetting({
          language: String(settings.language ?? "English")
        });
      }
      if (settings.reportTimezone) {
        await setReportTimezone(String(settings.reportTimezone));
      }
      if (settings.subscriptionUrl) {
        await setSubscriptionUrl(String(settings.subscriptionUrl));
      }
    } catch {
      // ignore parse errors
    }
  }

  if ((body as any)?.paypalApi) {
    await updatePaypalSetting(JSON.parse(String((body as any).paypalApi || "{}")));
  }
  if ((body as any)?.ccApi) {
    await updateCardsSetting(JSON.parse(String((body as any).ccApi || "{}")));
  }
  if ((body as any)?.walletApi) {
    await updateWalletsSetting(JSON.parse(String((body as any).walletApi || "{}")));
  }
  if ((body as any)?.storefrontApiAccessToken) {
    await updateStorefrontAccessToken(
      String((body as any).storefrontApiAccessToken), 
      String((body as any).storefrontPrivateToken ?? '')
    );
  }

  return {
    success: true,
    message: options?.successMsg || "Update shop info successfully",
    data: true,
  } as any;
}

export async function shopInfoById(id: string) {
  // 旧接口：/api/shops/shopInfoByShopifyId
  // 新文档：/shopify-app/shop
  // 这里返回 shopify-app 的 data（包含 installed/installation/shop 等）
  return request("/shopify-app/shop", {
    method: "GET",
    headers: {
      "shopify-id": id.split(".")[0],
    },
  });
}

export async function shopConfigById(id: string) {
  // 用于获取店铺域名等配置：从 /shopify-app/shop 的 shop 字段读取
  const data: any = await request("/shopify-app/shop", {
    method: "GET",
    headers: {
      "shopify-id": id.split(".")[0],
    },
  });
  // 尽量兼容旧使用方（期待 { domain }）
  const shop = data?.shop ?? {};
  return {
    ...shop,
    domain:
      shop?.domain ||
      shop?.myshopifyDomain ||
      shop?.myshopify_domain ||
      (data?.shopifyId ? String(data.shopifyId) : undefined),
  };
}

export async function shopInstall(id: string, params: ShopAPI.ShopInstall) {
  const { ...queryParams } = params;
  return request<any>(`/shopify-app/shop/install`, {
    method: "GET",
    headers: {
      "shopify-id": id,
    },
    params: {
      scope: queryParams.scope,
      shopifyId: id,
    },
  });
}

// 检查店铺是否安装
export async function checkShopInstall(id: string) {
  return request("/shopify-app/shop/check-installed", {
    method: "GET",
    headers: {
      "shopify-id": id,
    },
  });
}
